import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useCareTypeConfigContext } from '../../../Components/CareTypeConfigContext';
import { DatePicker, FormControl, Input } from '../../../Components/Form';
import { Modal } from '../../../Components/Modal/Modal';
import { FinalButton } from '../../../Components/FinalButton';
import { Modal as MantraModal, Text } from '../../../globalStyles';
import {
  CareType,
  Contract,
  ContractCareTypeScope,
  ContractCareTypeScopeInput,
  useCreateContractMutation,
  useEditContractScopeMutation,
} from '../../../graphQL';
import { Nullable } from '../../../types';
import { useOrganizationId } from '../../Organizations/util';

type EditableContract = Pick<Contract, 'id' | 'scope' | 'careTypes'>;

type AddEditModalProps = {
  contract?: Nullable<EditableContract>;
  careTypes?: CareType[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (contractId: number) => void;
};

type ContractScopeFormValues = {
  startDate?: EditableContract['scope']['startDate'];
  endDate?: EditableContract['scope']['endDate'];
  submit?: undefined;
} & Record<string, EditableContract['scope']['startDate'] | number | undefined>;

const getSessionsFieldName = (careType: CareType) => `${careType}Sessions`;

const getPatientsFieldName = (careType: CareType) => `${careType}Patients`;

const getContractCareTypeScope = (
  careTypes: readonly ContractCareTypeScope[],
  careType: CareType
): ContractCareTypeScope => {
  return (
    careTypes.find(contractCareType => contractCareType.careType === careType) ?? {
      careType,
      sessions: 0,
      patients: 0,
    }
  );
};

const buildContractModalFormValues = ({
  careTypes,
  contract,
}: {
  careTypes: CareType[];
  contract?: Nullable<EditableContract>;
}): ContractScopeFormValues => {
  const effectiveCareTypes = contract ? contract.careTypes : [];

  return careTypes.reduce<ContractScopeFormValues>(
    (values, careType) => {
      const careTypeScope = getContractCareTypeScope(effectiveCareTypes, careType);

      values[getSessionsFieldName(careType)] = careTypeScope.sessions;
      values[getPatientsFieldName(careType)] = careTypeScope.patients;

      return values;
    },
    {
      startDate: contract?.scope.startDate,
      endDate: contract?.scope.endDate,
      submit: undefined,
    }
  );
};

const buildContractScopeInput = (
  data: Record<string, unknown>,
  careTypes: CareType[]
): ContractCareTypeScopeInput[] => {
  return careTypes.map(careType => ({
    careType,
    sessions: Number(data[getSessionsFieldName(careType)] ?? 0),
    patients: Number(data[getPatientsFieldName(careType)] ?? 0),
  }));
};

const hasInvalidStudentCapacity = (data: Record<string, unknown>, careTypes: CareType[]) => {
  return careTypes.some(careType => {
    const sessions = Number(data[getSessionsFieldName(careType)] ?? 0);
    const patients = Number(data[getPatientsFieldName(careType)] ?? 0);
    return patients > sessions;
  });
};

export const AddEditContractModal = ({
  contract,
  careTypes,
  isOpen,
  onClose,
  onSave,
}: AddEditModalProps) => {
  const organizationId = useOrganizationId();
  const { getCareTypeDisplay } = useCareTypeConfigContext();
  const modalCareTypes = careTypes ?? [];

  const formValues = useMemo(
    () =>
      buildContractModalFormValues({
        careTypes: modalCareTypes,
        contract,
      }),
    [contract, modalCareTypes]
  );

  const formContext = useForm({
    defaultValues: formValues,
  });

  const {
    clearErrors,
    reset,
    trigger,
    formState: { errors },
  } = formContext;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    reset(formValues);
  }, [formValues, isOpen, reset]);

  const setFormError = (message: string) => {
    formContext.setError('submit', {
      type: 'custom',
      message,
    });
  };

  const [createContractMutation, { loading: createContractLoading }] = useCreateContractMutation({
    onCompleted: ({ createContract }) => {
      if (createContract.__typename === 'CreateContractError') {
        setFormError(createContract.message);
        return;
      }

      onSave(createContract.id);
      onClose();
    },
    onError: error => {
      setFormError(error.message);
    },
  });

  const [editContractMutation, { loading: editContractLoading }] = useEditContractScopeMutation({
    onCompleted: ({ editContractScope }) => {
      if (editContractScope.__typename === 'EditContractScopeError') {
        setFormError(editContractScope.message);
        return;
      }

      onSave(editContractScope.id);
      onClose();
    },
    onError: error => {
      setFormError(error.message);
    },
  });

  const submit = formContext.handleSubmit(async data => {
    clearErrors('submit');

    const isValid = await trigger();

    if (!isValid) {
      return;
    }

    if (!data.startDate || !data.endDate) {
      setFormError('Start Date and End Date are required');
      return;
    }

    if (hasInvalidStudentCapacity(data, modalCareTypes)) {
      setFormError('Student Capacity cannot be greater than Total Sessions');
      return;
    }

    if (moment(data.startDate).isAfter(data.endDate)) {
      setFormError('Start Date cannot be after End Date');
      return;
    }

    const contractInput = {
      startDate: data.startDate,
      endDate: data.endDate,
      careTypes: buildContractScopeInput(data, modalCareTypes),
    };

    if (contract) {
      await editContractMutation({
        variables: {
          input: {
            id: contract.id,
            ...contractInput,
          },
        },
      });
      return;
    }

    await createContractMutation({
      variables: {
        input: {
          organizationId,
          ...contractInput,
        },
      },
    });
  });

  const isSaving = createContractLoading || editContractLoading;
  const formControlsDisabled = isSaving;
  const saveDisabled = isSaving;
  const submitErrorMessage = errors.submit?.message;

  const careTypeInputs = modalCareTypes.map(careType => {
    const sessionsFieldName = getSessionsFieldName(careType);
    const patientsFieldName = getPatientsFieldName(careType);
    const careTypeLabel = getCareTypeDisplay(careType).serviceTitle;

    return (
      <tr key={careType}>
        <td className="tl pr5 ">{careTypeLabel}</td>
        <td className="pa2">
          <Controller
            name={sessionsFieldName}
            defaultValue={formValues[sessionsFieldName] ?? 0}
            render={({ onChange, value }) => (
              <Input
                className="tc"
                type="number"
                min={0}
                disabled={formControlsDisabled}
                aria-label={`${careTypeLabel} total sessions`}
                value={value ?? 0}
                onChange={e => {
                  const num = Number(e.currentTarget.value);
                  if (!Number.isNaN(num)) {
                    clearErrors('submit');
                    onChange(num);
                  }
                }}
              />
            )}
          />
        </td>
        <td className="pa2">
          <Controller
            name={patientsFieldName}
            defaultValue={formValues[patientsFieldName] ?? 0}
            render={({ onChange, value }) => (
              <Input
                className="tc"
                type="number"
                min={0}
                disabled={formControlsDisabled}
                aria-label={`${careTypeLabel} student capacity`}
                value={value ?? 0}
                onChange={e => {
                  const num = Number(e.currentTarget.value);
                  if (!Number.isNaN(num)) {
                    clearErrors('submit');
                    onChange(num);
                  }
                }}
              />
            )}
          />
        </td>
      </tr>
    );
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="small">
      <MantraModal.body>
        <div className="mb4">
          <Text.h2>Add/Edit Contract Year</Text.h2>
        </div>
        <FormProvider {...formContext}>
          <div className="flex items-center justify-between">
            <Text.body className="nowrap">Contract Start Date:</Text.body>
            <div>
              <FormControl name="startDate">
                <Controller
                  name="startDate"
                  defaultValue={formValues.startDate ?? null}
                  render={({ onChange, value }) => (
                    <DatePicker
                      value={value}
                      disabled={formControlsDisabled}
                      onChange={date => {
                        onChange(date);
                        clearErrors('submit');
                      }}
                      placeholder="MM/DD/YYYY"
                      iconRight="iconsCalendarCheckSvg"
                    />
                  )}
                />
              </FormControl>
            </div>
          </div>
          <div className="flex items-center justify-between mb4">
            <Text.body className="nowrap">Contract End Date:</Text.body>
            <div>
              <FormControl name="endDate">
                <Controller
                  name="endDate"
                  defaultValue={formValues.endDate ?? null}
                  render={({ onChange, value }) => (
                    <DatePicker
                      value={value}
                      disabled={formControlsDisabled}
                      onChange={date => {
                        onChange(date);
                        clearErrors('submit');
                      }}
                      placeholder="MM/DD/YYYY"
                      iconRight="iconsCalendarCheckSvg"
                    />
                  )}
                />
              </FormControl>
            </div>
          </div>

          <div className="mb3">
            <Text.h3>Sessions Configuration</Text.h3>
          </div>
          <form onSubmit={submit}>
            <table className="w-100">
              <thead>
                <tr>
                  <th className="tl pr2 ">Care Type</th>
                  <th>Total Sessions</th>
                  <th>Student Capacity</th>
                </tr>
              </thead>
              <tbody>{careTypeInputs}</tbody>
            </table>

            {submitErrorMessage && <Text.error className="pv2">{submitErrorMessage}</Text.error>}

            <div>
              <FinalButton
                type="submit"
                className="w-100"
                disabled={saveDisabled}
                loading={isSaving}
              >
                Save Changes
              </FinalButton>
              <FinalButton
                className="w-100"
                kind="minimal_gray"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </FinalButton>
            </div>
          </form>
        </FormProvider>
      </MantraModal.body>
    </Modal>
  );
};
